import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  jsonLd?: Record<string, any>;
}
const defaultSEO = {
  siteName: 'ADCC Bénin',
  title: 'ADCC Bénin - Formation & Insertion Professionnelle des Personnels de Maison | Association Dignité Compétence Création',
  description: "L'ADCC forme, certifie et place les personnels de maison au Bénin : entretien ménager, cuisine, garde d'enfants, gardiennage. Formation professionnelle certifiante à Cotonou pour un emploi digne et durable.",
  keywords: 'ADCC Bénin, formation personnel de maison Bénin, insertion professionnelle Cotonou, emploi domestique Bénin, formation ménage Cotonou, recrutement personnel maison Bénin, association formation Bénin, emploi digne Afrique, formation entretien ménager, garde enfants Cotonou, personnel qualifié Bénin, placement emploi Bénin',
  image: 'https://cdn.magicpatterns.com/uploads/qCb6hamLArrr2SMb6t4UDJ/ADCC_logo.png',
  url: 'https://adcc.africa',
  locale: 'fr_FR',
  language: 'fr',
  region: 'BJ',
  placename: 'Cotonou, Bénin',
  geo: {
    latitude: '6.3703',
    longitude: '2.3912'
  }
};
// Organization JSON-LD (appears on every page)
const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'NGO',
  name: 'ADCC - Association Dignité Compétence Création',
  alternateName: 'ADCC Bénin',
  url: 'https://adcc.africa',
  logo: 'https://cdn.magicpatterns.com/uploads/qCb6hamLArrr2SMb6t4UDJ/ADCC_logo.png',
  image: 'https://cdn.magicpatterns.com/uploads/qCb6hamLArrr2SMb6t4UDJ/ADCC_logo.png',
  description: 'Association béninoise pour la formation, la professionnalisation et le placement de personnels de maison et de services.',
  foundingDate: '2024',
  areaServed: {
    '@type': 'Country',
    name: 'Bénin'
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Cotonou',
    addressRegion: 'Littoral',
    addressCountry: 'BJ',
    streetAddress: 'Quartier Haie Vive'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+229-01-59-11-13-30',
    email: 'contact@adcc.africa',
    contactType: 'customer service',
    availableLanguage: 'French'
  },
  sameAs: ['https://www.facebook.com/profile.php?id=61587454706476', 'https://www.linkedin.com/company/association-dignit%C3%A9-comp%C3%A9tence-cr%C3%A9ation-adcc', 'https://www.instagram.com/adcc.benin'],
  knowsAbout: ['Formation professionnelle', 'Insertion professionnelle', 'Personnel de maison', 'Emploi domestique', 'Entretien ménager']
};
const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'ADCC Bénin',
  url: 'https://adcc.africa',
  description: defaultSEO.description,
  inLanguage: 'fr',
  publisher: {
    '@type': 'Organization',
    name: 'ADCC Bénin'
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://adcc.africa/?q={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
};
export function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  jsonLd
}: SEOProps) {
  const seo = {
    title: title ? `${title} | ADCC Bénin - Formation & Insertion Professionnelle` : defaultSEO.title,
    description: description || defaultSEO.description,
    keywords: keywords || defaultSEO.keywords,
    image: image || defaultSEO.image,
    url: url || defaultSEO.url,
    type
  };
  useEffect(() => {
    // Update document title
    document.title = seo.title;
    // Helper function to update or create meta tags
    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)?.[1] || '');
        } else if (selector.includes('http-equiv=')) {
          element.setAttribute('http-equiv', selector.match(/http-equiv="([^"]+)"/)?.[1] || '');
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };
    // Basic meta tags
    updateMetaTag('meta[name="description"]', 'content', seo.description);
    updateMetaTag('meta[name="keywords"]', 'content', seo.keywords);
    updateMetaTag('meta[name="author"]', 'content', 'ADCC Bénin');
    updateMetaTag('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('meta[name="googlebot"]', 'content', 'index, follow');
    // Language & Region targeting
    updateMetaTag('meta[http-equiv="content-language"]', 'content', 'fr');
    updateMetaTag('meta[name="language"]', 'content', 'French');
    updateMetaTag('meta[name="geo.region"]', 'content', 'BJ');
    updateMetaTag('meta[name="geo.placename"]', 'content', defaultSEO.placename);
    updateMetaTag('meta[name="geo.position"]', 'content', `${defaultSEO.geo.latitude};${defaultSEO.geo.longitude}`);
    updateMetaTag('meta[name="ICBM"]', 'content', `${defaultSEO.geo.latitude}, ${defaultSEO.geo.longitude}`);
    // Theme & Mobile
    updateMetaTag('meta[name="theme-color"]', 'content', '#2C3E50');
    updateMetaTag('meta[name="msapplication-TileColor"]', 'content', '#2C3E50');
    updateMetaTag('meta[name="format-detection"]', 'content', 'telephone=yes');
    // Open Graph tags (Facebook, LinkedIn, WhatsApp)
    updateMetaTag('meta[property="og:title"]', 'content', seo.title);
    updateMetaTag('meta[property="og:description"]', 'content', seo.description);
    updateMetaTag('meta[property="og:image"]', 'content', seo.image);
    updateMetaTag('meta[property="og:image:width"]', 'content', '1200');
    updateMetaTag('meta[property="og:image:height"]', 'content', '630');
    updateMetaTag('meta[property="og:image:alt"]', 'content', 'ADCC Bénin - Formation et Insertion Professionnelle');
    updateMetaTag('meta[property="og:url"]', 'content', seo.url);
    updateMetaTag('meta[property="og:type"]', 'content', seo.type);
    updateMetaTag('meta[property="og:site_name"]', 'content', defaultSEO.siteName);
    updateMetaTag('meta[property="og:locale"]', 'content', defaultSEO.locale);
    // Twitter Card tags (kept for link preview support, without account reference)
    updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', 'content', seo.title);
    updateMetaTag('meta[name="twitter:description"]', 'content', seo.description);
    updateMetaTag('meta[name="twitter:image"]', 'content', seo.image);
    updateMetaTag('meta[name="twitter:image:alt"]', 'content', 'ADCC Bénin - Formation et Insertion Professionnelle');
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seo.url);
    // Hreflang for French
    let hreflang = document.querySelector('link[hreflang="fr"]') as HTMLLinkElement;
    if (!hreflang) {
      hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', 'fr');
      document.head.appendChild(hreflang);
    }
    hreflang.setAttribute('href', seo.url);
    // x-default hreflang
    let hreflangDefault = document.querySelector('link[hreflang="x-default"]') as HTMLLinkElement;
    if (!hreflangDefault) {
      hreflangDefault = document.createElement('link');
      hreflangDefault.setAttribute('rel', 'alternate');
      hreflangDefault.setAttribute('hreflang', 'x-default');
      document.head.appendChild(hreflangDefault);
    }
    hreflangDefault.setAttribute('href', seo.url);
    // Preconnect hints for performance
    const preconnectUrls = ['https://cdn.magicpatterns.com', 'https://images.unsplash.com', 'https://fonts.googleapis.com'];
    preconnectUrls.forEach(href => {
      if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    });
    // JSON-LD Structured Data
    // Remove old JSON-LD scripts
    document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
    // Add Organization schema
    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.setAttribute('data-seo-jsonld', 'organization');
    orgScript.textContent = JSON.stringify(organizationJsonLd);
    document.head.appendChild(orgScript);
    // Add WebSite schema
    const siteScript = document.createElement('script');
    siteScript.type = 'application/ld+json';
    siteScript.setAttribute('data-seo-jsonld', 'website');
    siteScript.textContent = JSON.stringify(websiteJsonLd);
    document.head.appendChild(siteScript);
    // Add page-specific JSON-LD if provided
    if (jsonLd) {
      const pageScript = document.createElement('script');
      pageScript.type = 'application/ld+json';
      pageScript.setAttribute('data-seo-jsonld', 'page');
      pageScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(pageScript);
    }
    // Add BreadcrumbList
    if (seo.url !== defaultSEO.url) {
      const pageName = title || 'Page';
      const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [{
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: 'https://adcc.africa'
        }, {
          '@type': 'ListItem',
          position: 2,
          name: pageName,
          item: seo.url
        }]
      };
      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.setAttribute('data-seo-jsonld', 'breadcrumb');
      breadcrumbScript.textContent = JSON.stringify(breadcrumbJsonLd);
      document.head.appendChild(breadcrumbScript);
    }
    // Cleanup on unmount
    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
    };
  }, [seo.title, seo.description, seo.keywords, seo.image, seo.url, seo.type, title, jsonLd]);
  return null;
}
// Page-specific SEO configurations with optimized keywords
export const pageSEO = {
  home: {
    title: undefined,
    description: "L'ADCC forme, certifie et place les personnels de maison au Bénin : entretien ménager, cuisine, garde d'enfants, gardiennage. Formation professionnelle à Cotonou pour un emploi digne et durable.",
    keywords: 'ADCC Bénin, formation personnel de maison Cotonou, insertion professionnelle Bénin, emploi domestique Bénin, recrutement personnel qualifié, formation ménage Bénin, association formation professionnelle Afrique, placement emploi digne',
    url: 'https://adcc.africa'
  },
  about: {
    title: 'À Propos - Notre Mission',
    description: "Découvrez l'ADCC, association béninoise fondée pour la dignité et l'insertion professionnelle des personnels de maison. Notre histoire, notre mission, nos valeurs et notre équipe à Cotonou.",
    keywords: 'ADCC histoire, association Bénin, mission insertion professionnelle, personnels de maison Cotonou, dignité travail domestique, ONG formation Bénin, équipe ADCC',
    url: 'https://adcc.africa/about'
  },
  actions: {
    title: 'Nos Actions - Formations & Événements',
    description: "Formations professionnelles certifiantes, ateliers pratiques, festival des personnels de maison, placement en emploi. Découvrez les actions de l'ADCC au Bénin en 2026.",
    keywords: 'formation professionnelle Cotonou 2026, atelier entretien ménager Bénin, festival personnel de maison, inscription formation Bénin, certification professionnelle Afrique, placement emploi Cotonou',
    url: 'https://adcc.africa/actions'
  },
  impact: {
    title: 'Notre Impact - Résultats & Objectifs',
    description: "Objectifs 2026 de l'ADCC : 100 personnes formées, 60% de taux de placement, suivi post-insertion. Découvrez notre impact mesurable pour l'insertion professionnelle au Bénin.",
    keywords: 'impact social Bénin, résultats formation professionnelle, taux placement emploi, objectifs ADCC 2026, insertion durable Afrique, indicateurs impact social',
    url: 'https://adcc.africa/impact'
  },
  blog: {
    title: 'Actualités & Événements',
    description: "Actualités de l'ADCC : forum fondateur, ateliers de formation mars 2026, festival des personnels de maison juin 2026. Suivez nos avancées au Bénin.",
    keywords: 'actualités ADCC Bénin, forum personnel de maison, formation mars 2026 Cotonou, festival juin 2026 Bénin, événements insertion professionnelle, blog association Bénin',
    url: 'https://adcc.africa/blog'
  },
  donate: {
    title: 'Faire un Don - Soutenez la Formation',
    description: "Soutenez l'ADCC par un don : financez la formation et le placement de personnels de maison au Bénin. Paiement sécurisé par carte bancaire (Stripe) ou PayPal. Chaque don compte.",
    keywords: 'don ADCC Bénin, soutenir formation professionnelle, financer insertion emploi, don association Afrique, aide personnel de maison, donation Stripe PayPal',
    url: 'https://adcc.africa/donate'
  },
  partner: {
    title: 'Devenir Partenaire',
    description: "Rejoignez le réseau de partenaires de l'ADCC. Entreprises, institutions, ONG : collaborons pour la formation et l'insertion professionnelle digne des personnels de maison au Bénin.",
    keywords: 'partenariat ADCC Bénin, partenaire formation professionnelle, RSE Afrique, collaboration ONG Bénin, mécénat insertion professionnelle, entreprise partenaire Cotonou',
    url: 'https://adcc.africa/partner'
  }
};